import {
  Image,
  PDFDownloadLink,
  StyleSheet,
  Text,
  View,
} from "@react-pdf/renderer";
import { AlertCircle, Download, Loader2 } from "lucide-react";
import React, { useCallback, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { getPrescriptionFormByAppointmentId_apiCalls } from "../../api/api_calls/appointmentforms/prescriptionForm_apiCalls";
import DocMobileLogo from "../../assets/images/DocMobilLogo.png";
import { Button } from "../ui/button";
import GenericPDFDocument from "./GenericPDFDocument";

const styles = StyleSheet.create({
  page: {
    padding: 40,
    position: "relative",
    fontFamily: "Helvetica",
    minHeight: "100%",
  },
  watermarkContainer: {
    position: "absolute",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    opacity: 0.3,
    zIndex: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  watermarkImage: {
    width: 200,
    height: 200,
    objectFit: "contain",
  },
  content: {
    position: "relative",
    zIndex: 1,
  },
  heading: {
    fontSize: 22,
    fontWeight: 700,
    marginBottom: 16,
  },
  prescriptionBlock: {
    marginBottom: 18,
    paddingBottom: 8,
    borderBottom: "1px solid #ccc",
  },
  label: {
    fontSize: 12,
    color: "#888",
    fontWeight: 500,
  },
  value: {
    fontSize: 14,
    color: "#222",
    fontWeight: 600,
    marginBottom: 4,
  },
  instructions: {
    fontSize: 12,
    color: "#444",
    marginTop: 4,
  },
});

const PrescriptionBlockPDF = ({ prescription, isLast }) => (
  <View
    style={{
      ...styles.prescriptionBlock,
      borderBottom: isLast ? undefined : styles.prescriptionBlock.borderBottom,
    }}
  >
    <View style={{ flexDirection: "row", width: "100%" }}>
      <View style={{ flex: 1, gap: 4 }}>
        <Text style={styles.label}>
          Drug Name:{" "}
          <Text style={styles.value}>
            {prescription.drugName || "Not specified"}
          </Text>
        </Text>
        <Text style={styles.label}>
          Dosage:{" "}
          <Text style={styles.value}>
            {prescription.dosage || "Not specified"}
          </Text>
        </Text>
      </View>
      <View style={{ flex: 1, gap: 4 }}>
        <Text style={styles.label}>
          Duration:{" "}
          <Text style={styles.value}>
            {prescription.duration || "Not specified"}
          </Text>
        </Text>
        <Text style={styles.label}>
          Frequency:{" "}
          <Text style={styles.value}>
            {prescription.frequency || "Not specified"}
          </Text>
        </Text>
      </View>
    </View>
    {prescription.instructions && (
      <View style={{ marginTop: 6 }}>
        <Text style={styles.label}>Instructions:</Text>
        <Text style={styles.instructions}>{prescription.instructions}</Text>
      </View>
    )}
  </View>
);

const PrescriptionFilled = ({ printRef }) => {
  const location = useLocation();

  // Get appointmentId from location state
  const appointmentId =
    location.state?.appointmentId || location.state?.appointment?.id;

  const [prescriptionData, setPrescriptionData] = useState([]);
  const [loading, setLoading] = useState(true);

  // Fetch existing prescription form data
  const fetchPrescriptionForm = useCallback(async () => {
    if (!appointmentId) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const response = await getPrescriptionFormByAppointmentId_apiCalls({
        appointmentId,
      });
      console.log("response", response);
      if (response?.data) {
        setPrescriptionData(response.data);
      }
    } catch (error) {
      console.error("Failed to fetch prescription form:", error);
      // Don't show error for missing prescription form, just show empty state
    } finally {
      setLoading(false);
    }
  }, [appointmentId]);

  // Effect to fetch prescription form on component mount
  useEffect(() => {
    fetchPrescriptionForm();
  }, [fetchPrescriptionForm]);

  // Loading state
  if (loading) {
    return (
      <div className="w-full">
        <div className="flex justify-center items-center py-8">
          <Loader2 className="h-6 w-6 animate-spin mr-2" />
          <span>Loading prescription data...</span>
        </div>
      </div>
    );
  }

  // No appointment ID provided
  if (!appointmentId) {
    return (
      <div className="w-full">
        <div className="flex flex-col items-center justify-center py-8 text-center">
          <AlertCircle className="w-12 h-12 text-muted-foreground mb-2" />
          <h3 className="text-lg font-medium">No Appointment ID</h3>
          <p className="text-sm text-muted-foreground">
            Unable to load prescription data without appointment information.
          </p>
        </div>
      </div>
    );
  }

  // Empty state
  if (prescriptionData.length === 0) {
    return (
      <div className="w-full">
        <div className="flex w-full items-center justify-between flex-row mb-6">
          <h2 className="text-xl font-semibold">Prescription</h2>
        </div>
        <div className="flex flex-col items-center justify-center py-8 text-center border border-dashed rounded-lg">
          <AlertCircle className="w-12 h-12 text-muted-foreground mb-2" />
          <h3 className="text-lg font-medium">No perscriptionData found</h3>
          <p className="text-sm text-muted-foreground">
            No perscriptionData were added for this appointment.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full gap-[24px] relative" ref={printRef}>
      {/* Download PDF Button */}
      <div className="flex justify-between mb-4">
        <div className="flex w-full items-center justify-between flex-row">
          <h2 className="text-xl font-semibold">Prescription</h2>
        </div>
        <PDFDownloadLink
          document={
            <GenericPDFDocument heading="Prescription">
              {prescriptionData.map((prescription, idx) => (
                <PrescriptionBlockPDF
                  key={idx}
                  prescription={prescription}
                  isLast={idx === prescriptionData.length - 1}
                />
              ))}
              <View
                key="signature-row"
                style={{
                  flexDirection: "row",
                  justifyContent: "space-between",
                  marginTop: 20,
                }}
              >
                <View
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "flex-start",
                    width: "50%",
                  }}
                >
                  <Text
                    style={{
                      fontSize: 16,
                      fontWeight: 700,
                      color: "#1E1E1E",
                      marginBottom: "8px",
                      textAlign: "left",
                    }}
                  >
                    Signature
                  </Text>
                  {prescriptionData?.[0]?.appointment?.doctor?.signature
                    ?.url ? (
                    <View style={{ width: 150, marginLeft: -20 }}>
                      <Image
                        src={`${prescriptionData?.[0]?.appointment?.doctor?.signature?.url}?ext=.png`}
                        style={{ width: 150, objectFit: "contain" }}
                      />
                    </View>
                  ) : (
                    <Text style={{ fontSize: 12, color: "#888" }}>
                      No signature available
                    </Text>
                  )}
                </View>
                <View>
                  <Text style={{ fontSize: 14, fontWeight: 600 }}>
                    Created Date
                  </Text>
                  <Text style={{ fontSize: 14, fontWeight: 600 }}>
                    {prescriptionData?.[0]?.createdAt
                      ? new Date(
                          prescriptionData?.[0]?.createdAt,
                        ).toLocaleDateString("en-US")
                      : "N/A"}
                  </Text>
                </View>
              </View>
            </GenericPDFDocument>
          }
          fileName={`prescription_${appointmentId}.pdf`}
        >
          {({ loading: pdfLoading }) => (
            <Button variant="primary" disabled={pdfLoading}>
              <Download className="mr-2 h-4 w-4" />
              {pdfLoading ? "Preparing PDF..." : "Download"}
            </Button>
          )}
        </PDFDownloadLink>
      </div>
      {/* Watermark Logo (for on-screen view only) */}
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none select-none opacity-40 z-0">
        <img
          src={DocMobileLogo}
          alt="DocMobil Logo"
          className="max-w-xs w-full h-auto"
        />
      </div>

      <div className="space-y-6">
        {prescriptionData.map((prescription, index) => (
          <div
            key={prescription.id || index}
            className={`flex flex-col justify-between w-full py-[8px] gap-[12px]${index !== prescriptionData.length - 1 ? " border-b-[1px] border-solid border-b-[1E1E1E80]" : ""}`}
          >
            <div className="flex flex-row items-center w-full">
              <div className="space-y-2 text-[16px] w-full md:w-1/2 ">
                <p className=" text-[#1E1E1EB2] font-medium">
                  Drug Name:{" "}
                  <strong className="text-[#1E1E1E] font-semibold">
                    {prescription.drugName || "Not specified"}
                  </strong>
                </p>
                <p className=" text-[#1E1E1EB2] font-medium">
                  Dosage: {""}
                  <strong className="text-[#1E1E1E] font-semibold">
                    {prescription.dosage || "Not specified"}
                  </strong>
                </p>
              </div>

              <div className="space-y-2 w-full md:w-1/2 font-semibold">
                <p className=" text-[#1E1E1EB2] font-medium">
                  Duration:{" "}
                  <span className="text-[#1E1E1E]">
                    {prescription.duration || "Not specified"}
                  </span>
                </p>
                <p className=" text-[#1E1E1EB2] font-medium">
                  Frequency:{" "}
                  <span className=" text-[#1E1E1E]">
                    {prescription.frequency || "Not specified"}
                  </span>
                </p>
              </div>
            </div>
            {prescription.instructions && (
              <div>
                <p className=" text-[#1E1E1EB2] font-medium">Instructions:</p>
                <p className=" text-[#1E1E1E]">{prescription.instructions}</p>
              </div>
            )}
          </div>
        ))}
      </div>
      <div className="flex justify-between flex-row mt-5">
        <div className="">
          <p className="text-xl font-bold text-[#1E1E1E] mb-2">Signature</p>
          <div className="font-script text-lg">
            <img
              alt="Medical Officer Signature"
              style={{ width: "150px", height: "50px" }}
              src={prescriptionData?.[0]?.appointment?.doctor?.signature?.url}
            />
          </div>
        </div>
        <div style={{ gap: "10px", display: "flex", flexDirection: "column" }}>
          <p className="font-[600] text-[16px]">Created Date</p>
          <p className="font-[600] text-[16px]">
            {prescriptionData?.[0]?.createdAt
              ? new Date(prescriptionData?.[0]?.createdAt).toLocaleDateString()
              : "N/A"}
          </p>
        </div>
      </div>
    </div>
  );
};

export default PrescriptionFilled;
